import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

const TENANT_TYPES = ['ENTERPRISE', 'BUSINESS', 'PARTNER', 'RESELLER', 'INTERNAL', 'TRIAL'];
const ISOLATION = ['SHARED', 'DEDICATED_SCHEMA', 'DEDICATED_DATABASE', 'HYBRID'];
const PLANS = ['TRIAL', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE', 'CUSTOM'];

interface CreatedTenant {
  readonly saasTenantId: string;
  readonly tenantCode: string;
  readonly tenantName: string;
  readonly status: string;
}

/**
 * M43 §27 Tenant Provisioning Wizard (§8 Provisioning Framework). A guided
 * three-step flow: (1) capture the tenant details and create the tenant record,
 * (2) run provisioning (stands up the default subscription + security posture),
 * (3) activate the tenant. Each step calls the lifecycle API and requires its
 * distinct SoD permission (TENANT_MANAGE → TENANT_PROVISION → TENANT_ACTIVATE).
 */
export function TenantProvisioningWizard(): ReactNode {
  const { token } = useAuth();
  const [step, setStep] = useState(1);
  const [tenant, setTenant] = useState<CreatedTenant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [tenantName, setTenantName] = useState('');
  const [tenantType, setTenantType] = useState('BUSINESS');
  const [isolationModel, setIsolationModel] = useState('SHARED');
  const [subscriptionPlan, setSubscriptionPlan] = useState('TRIAL');
  const [contactEmail, setContactEmail] = useState('');

  const forbid = (err: unknown, perm: string): void => {
    if (err instanceof ApiError && err.status === 403) setError(`You are not authorised for this step (${perm} required).`);
    else setError((err as Error).message);
  };

  const createTenant = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!tenantName.trim()) { setError('A tenant name is required.'); return; }
    if (!contactEmail.trim()) { setError('A primary contact email is required.'); return; }
    try {
      const r = await apiRequest<{ data: CreatedTenant }>('/v1/saas/tenants', {
        method: 'POST',
        body: {
          tenantName: tenantName.trim(),
          tenantType,
          isolationModel,
          subscriptionPlan,
          primaryContactEmail: contactEmail.trim(),
        },
        token,
      });
      setTenant(r.data);
      setNotice('Step 1 complete — tenant created.');
      setStep(2);
    } catch (err) { forbid(err, 'SAAS_TENANT_ADMIN_TENANT_MANAGE'); }
  };

  const provision = async (): Promise<void> => {
    if (!tenant) return;
    setError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/saas/tenants/${tenant.saasTenantId}/verify`, { method: 'POST', body: {}, token });
      const r = await apiRequest<{ data: CreatedTenant }>(`/v1/saas/tenants/${tenant.saasTenantId}/provision`, { method: 'POST', body: {}, token });
      setTenant(r.data);
      setNotice('Step 2 complete — tenant provisioned.');
      setStep(3);
    } catch (err) { forbid(err, 'SAAS_TENANT_ADMIN_TENANT_PROVISION'); }
  };

  const activate = async (): Promise<void> => {
    if (!tenant) return;
    setError(null);
    setNotice(null);
    try {
      const r = await apiRequest<{ data: CreatedTenant }>(`/v1/saas/tenants/${tenant.saasTenantId}/activate`, { method: 'POST', body: {}, token });
      setTenant(r.data);
      setNotice('Step 3 complete — tenant activated.');
      setStep(4);
    } catch (err) { forbid(err, 'SAAS_TENANT_ADMIN_TENANT_ACTIVATE'); }
  };

  return (
    <main aria-label="tenant-provisioning-wizard">
      <h1>Tenant Provisioning Wizard</h1>
      <p aria-label="wizard-step">Step {step} of 3</p>
      {error && <p role="alert" aria-label="wizard-error">{error}</p>}
      {notice && <p aria-label="wizard-notice">{notice}</p>}

      {step === 1 && (
        <section aria-label="wizard-step-1">
          <h2>1. Tenant Details</h2>
          <form aria-label="wizard-form" onSubmit={(e) => void createTenant(e)}>
            <label>Name<input aria-label="field-tenant-name" value={tenantName} onChange={(e) => setTenantName(e.target.value)} /></label>
            <label>Type
              <select aria-label="field-tenant-type" value={tenantType} onChange={(e) => setTenantType(e.target.value)}>
                {TENANT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label>Isolation
              <select aria-label="field-isolation" value={isolationModel} onChange={(e) => setIsolationModel(e.target.value)}>
                {ISOLATION.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label>Plan
              <select aria-label="field-plan" value={subscriptionPlan} onChange={(e) => setSubscriptionPlan(e.target.value)}>
                {PLANS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label>Contact email<input aria-label="field-contact-email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} /></label>
            <button type="submit" aria-label="submit-create">Create Tenant</button>
          </form>
        </section>
      )}

      {step === 2 && tenant && (
        <section aria-label="wizard-step-2">
          <h2>2. Provision</h2>
          <p aria-label="wizard-tenant-code">{tenant.tenantCode}</p>
          <p aria-label="wizard-tenant-status">{tenant.status}</p>
          <button type="button" aria-label="submit-provision" onClick={() => void provision()}>Provision Tenant</button>
        </section>
      )}

      {step === 3 && tenant && (
        <section aria-label="wizard-step-3">
          <h2>3. Activate</h2>
          <p aria-label="wizard-tenant-status">{tenant.status}</p>
          <button type="button" aria-label="submit-activate" onClick={() => void activate()}>Activate Tenant</button>
        </section>
      )}

      {step === 4 && tenant && (
        <section aria-label="wizard-complete">
          <h2>Complete</h2>
          <p aria-label="wizard-final-status">{tenant.status}</p>
        </section>
      )}
    </main>
  );
}
